// ══════════════════════════════════════════════════════════════
// COMPASS – Process Actions
// ══════════════════════════════════════════════════════════════
import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { KycConsentEvidenceDto } from '../../api/models/kyc-consent-evidence-dto';
import { KycProcessDto } from '../../api/models/kyc-process-dto';
import { KycProcessWithoutClientCreateDto } from '../../api/models/kyc-process-without-client-create-dto';
import { KycProcessListRow } from './process.state';
import { GetStatistics$Params } from '../../api/fn/kyc-process/get-statistics';
import { KycPersonFormDto } from '../../api/models/kyc-person-form-dto';
import { KycCompanyFormDto } from '../../api/models/kyc-company-form-dto';
import { KycChangeRequestWithItemsDto } from '../../api/models/kyc-change-request-with-items-dto';
import { KycChangeRequestDto } from '../../api/models/kyc-change-request-dto';
import { KycProcessStatusTransitionRequestDto } from '../../api/models/kyc-process-status-transition-request-dto';
import { KycProcessReopenRequestDto } from '../../api/models/kyc-process-reopen-request-dto';
import { KycProcessSubmitRequestDto } from '../../api/models/kyc-process-submit-request-dto';
import { KycFormSnapshotSummaryDto } from '../../api/models/kyc-form-snapshot-summary-dto';
import { KycFormSnapshotDto } from '../../api/models/kyc-form-snapshot-dto';
import { KycProcessApprovalStateDto } from '../../api/models/kyc-process-approval-state-dto';
import { KycProcessApprovalDto } from '../../api/models/kyc-process-approval-dto';
import { KycApprovalDecisionRequestDto } from '../../api/models/kyc-approval-decision-request-dto';
import { KycWorkflowEventDto } from '../../api/models/kyc-workflow-event-dto';
import { KycProcessParticipantDto } from '../../api/models/kyc-process-participant-dto';
import { ClientRiskMatrixStateResponseDto } from '../../api/models/client-risk-matrix-state-response-dto';
import { ClientRiskMatrixAssessRequestDto } from '../../api/models/client-risk-matrix-assess-request-dto';
import { ClientRiskMatrixAssessResponseDto } from '../../api/models/client-risk-matrix-assess-response-dto';
import { ClientRiskMatrixMeasuresRequestDto } from '../../api/models/client-risk-matrix-measures-request-dto';
import { ClientRiskMatrixMeasuresResponseDto } from '../../api/models/client-risk-matrix-measures-response-dto';
import { ClientRiskMatrixResultsRequestDto } from '../../api/models/client-risk-matrix-results-request-dto';
import { ClientRiskMatrixResultsResponseDto } from '../../api/models/client-risk-matrix-results-response-dto';
import { GroupedScreeningsDto } from '../../api/models/grouped-screenings-dto';
import { ScreeningsRunResponseDto } from '../../api/models/screenings-run-response-dto';
import { WorldCheckOneCaseDto } from '../../api/models/world-check-one-case-dto';
import { MissingTargetDto } from '../../api/models/missing-target-dto';
import { KycProcessStatisticsDto } from '../../api/models/kyc-process-statistics-dto';
import { KycReportDto } from '../../api/models/kyc-report-dto';
import { KycComplianceReportDto } from '../../api/models/kyc-compliance-report-dto';
import { EntrustSignatureStatusResponseDto } from '../../api/models/entrust-signature-status-response-dto';
import { EntrustSignatureStartResponseDto } from '../../api/models/entrust-signature-start-response-dto';
import { EntrustSignatureRequestDto } from '../../api/models/entrust-signature-request-dto';
import { EntrustSignatureEvidenceFolderDto } from '../../api/models/entrust-signature-evidence-folder-dto';
import { ConflictedForm } from './process-form-conflict';
import { ProcessObservationKind } from '../../models/process-observation.model';

export type ProcessStatisticsFilters = Pick<
  GetStatistics$Params,
  'statuses' | 'riskLevels' | 'clientTypes' | 'fundNames' | 'clientName'
>;

export const ProcessActions = createActionGroup({
  source: 'Process',
  events: {
    // Load all processes (role-based: CLIENT calls /me, others call /kyc/processes)
    'Load Processes': props<{ managementCompanyId: string }>(),
    'Load Processes Success': props<{ processes: KycProcessListRow[] }>(),
    'Load Processes Failure': props<{ error: string }>(),

    // Select a process from the already-loaded list (no API call)
    'Select Process': props<{ processId: string }>(),

    // Load a single process by ID (API call – used for deep links)
    'Load Process': props<{ processId: string }>(),
    'Load Process Success': props<{ process: KycProcessDto }>(),
    // `status` lets the page tell a missing or deleted process (404 / 403) from a failed load (US-025).
    'Load Process Failure': props<{ error: string; status?: number }>(),

    // Lightweight refresh: re-fetches the process but only updates
    // `selectedProcess` (preserves changeRequests, timeline, forms, etc.).
    // Used after comment/approval actions to pick up backend-driven status
    // transitions (e.g. DRAFT → CHANGES_REQUESTED).
    'Refresh Process Status': props<{ processId: string }>(),
    'Refresh Process Status Success': props<{ process: KycProcessDto }>(),
    'Refresh Process Status Failure': props<{ error: string }>(),

    // Load individual KYC form
    'Load Individual Form': props<{ processId: string }>(),
    'Load Individual Form Success': props<{ form: KycPersonFormDto }>(),
    'Load Individual Form Failure': props<{ error: string }>(),

    // Save individual KYC form
    'Save Individual Form': props<{ processId: string; form: KycPersonFormDto; silent?: boolean }>(),
    'Save Individual Form Success': props<{ form: KycPersonFormDto }>(),
    // `code` set for a 409 form conflict (FORM_CONFLICT_CODES): no toast, Form Save Conflict follows.
    'Save Individual Form Failure': props<{ error: string; code?: string }>(),

    // Load company KYC form
    'Load Company Form': props<{ processId: string; silent?: boolean }>(),
    'Load Company Form Success': props<{ form: KycCompanyFormDto }>(),
    'Load Company Form Failure': props<{ error: string; silent?: boolean }>(),

    // Save company KYC form
    'Save Company Form': props<{ processId: string; form: KycCompanyFormDto; silent?: boolean }>(),
    'Save Company Form Success': props<{ form: KycCompanyFormDto }>(),
    'Save Company Form Failure': props<{ error: string; code?: string }>(),

    // US-024: a form save was refused because someone else saved the form since it was loaded (409 stale-version /
    // concurrent-modification). The process page asks the user to reload it.
    'Form Save Conflict': props<{ processId: string; formType: ConflictedForm }>(),

    // Change Requests
    'Load Change Requests': props<{ processId: string }>(),
    'Load Change Requests Success': props<{ changeRequests: KycChangeRequestWithItemsDto[] }>(),
    'Load Change Requests Failure': props<{ error: string }>(),

    'Create Change Request': props<{ processId: string; body: KycChangeRequestWithItemsDto }>(),
    'Create Change Request Success': props<{ changeRequest: KycChangeRequestWithItemsDto }>(),
    'Create Change Request Failure': props<{ error: string }>(),

    'Close Change Request': props<{ processId: string; changeRequestId: string }>(),
    'Close Change Request Success': props<{ changeRequest: KycChangeRequestDto }>(),
    'Close Change Request Failure': props<{ error: string }>(),

    'Reopen Change Request': props<{ processId: string; changeRequestId: string }>(),
    'Reopen Change Request Success': props<{ changeRequest: KycChangeRequestDto }>(),
    'Reopen Change Request Failure': props<{ error: string }>(),

    // Status Transition
    'Transition Status': props<{ processId: string; body: KycProcessStatusTransitionRequestDto }>(),
    'Transition Status Success': props<{ process: KycProcessDto }>(),
    'Transition Status Failure': props<{ error: string }>(),

    // Compliance Officer reopen (FOR_APPROVAL | COMPLETED → IN_PROGRESS)
    'Reopen Process': props<{ processId: string; body: KycProcessReopenRequestDto }>(),
    'Reopen Process Success': props<{ process: KycProcessDto }>(),
    'Reopen Process Failure': props<{ error: string }>(),

    // Client reopen (FOR_APPROVAL | COMPLETED before the signature → IN_PROGRESS); code is the backend error code
    'Client Reopen Process': props<{ processId: string }>(),
    'Client Reopen Process Success': props<{ process: KycProcessDto }>(),
    'Client Reopen Process Failure': props<{ processId: string; error: string; code?: string }>(),

    // Form version history (submitted snapshots)
    'Load Form Versions': props<{ processId: string }>(),
    'Load Form Versions Success': props<{ versions: KycFormSnapshotSummaryDto[] }>(),
    'Load Form Versions Failure': props<{ error: string }>(),

    'Load Form Version Detail': props<{ processId: string; snapshotId: string }>(),
    'Load Form Version Detail Success': props<{ version: KycFormSnapshotDto }>(),
    'Load Form Version Detail Failure': props<{ error: string }>(),
    'Clear Form Version Detail': emptyProps(),

    // US-026: a Compliance Officer creates a process without a client (origin COMPLIANCE). The failure has no toast:
    // the dialog shows it, with the backend's field errors on their fields.
    'Create Process Without Client': props<{ body: KycProcessWithoutClientCreateDto }>(),
    'Create Process Without Client Success': props<{ process: KycProcessDto }>(),
    'Create Process Without Client Failure': props<{ error: string; fieldErrors?: Record<string, string> }>(),

    // US-025: a Compliance Officer deletes a process (soft delete) until its signature is requested, with a reason
    // that is never shown to the client. The dialog shows a failure; `code` tells the page to reload or leave.
    'Delete Process': props<{ processId: string; reason: string }>(),
    'Delete Process Success': props<{ processId: string }>(),
    'Delete Process Failure': props<{ processId: string; error: string; status?: number; code?: string }>(),

    // Submit Process
    'Submit Process': props<{ processId: string; body: KycProcessSubmitRequestDto }>(),
    'Submit Process Success': props<{ process: KycProcessDto }>(),
    'Submit Process Failure': props<{ error: string }>(),

    // Approval Workflow
    'Load Approval State': props<{ processId: string }>(),
    'Load Approval State Success': props<{ approvalState: KycProcessApprovalStateDto }>(),
    'Load Approval State Failure': props<{ error: string }>(),

    'Load Approvals': props<{ processId: string }>(),
    'Load Approvals Success': props<{ approvals: KycProcessApprovalDto[] }>(),
    'Load Approvals Failure': props<{ error: string }>(),

    'Decide Approval': props<{ processId: string; body: KycApprovalDecisionRequestDto }>(),
    'Decide Approval Success': props<{ approval: KycProcessApprovalDto }>(),
    'Decide Approval Failure': props<{ error: string }>(),

    // Timeline
    'Load Timeline': props<{ processId: string }>(),
    'Load Timeline Success': props<{ events: KycWorkflowEventDto[] }>(),
    'Load Timeline Failure': props<{ error: string }>(),

    // Participants
    'Load Participants': props<{ processId: string }>(),
    'Load Participants Success': props<{ participants: KycProcessParticipantDto[] }>(),
    'Load Participants Failure': props<{ error: string }>(),

    // Risk Matrix
    'Load Risk Matrix State': props<{ processId: string }>(),
    'Load Risk Matrix State Success': props<{ riskMatrixState: ClientRiskMatrixStateResponseDto }>(),
    'Load Risk Matrix State Failure': props<{ error: string }>(),

    'Assess Risk Matrix': props<{ processId: string; body: ClientRiskMatrixAssessRequestDto }>(),
    'Assess Risk Matrix Success': props<{ response: ClientRiskMatrixAssessResponseDto }>(),
    'Assess Risk Matrix Failure': props<{ error: string }>(),

    // Risk Measures (due-diligence measures of the latest assessment)
    'Load Risk Measures': props<{ processId: string }>(),
    'Load Risk Measures Success': props<{ riskMeasures: ClientRiskMatrixMeasuresResponseDto }>(),
    'Load Risk Measures Failure': props<{ error: string }>(),

    'Save Risk Measures': props<{ processId: string; body: ClientRiskMatrixMeasuresRequestDto }>(),
    'Save Risk Measures Success': props<{ riskMeasures: ClientRiskMatrixMeasuresResponseDto }>(),
    'Save Risk Measures Failure': props<{ processId: string; error: string; code?: string }>(),

    // Risk assessment results set by hand (US-011): the whole results panel, replacing the calculated one
    'Save Risk Results': props<{ processId: string; body: ClientRiskMatrixResultsRequestDto }>(),
    'Save Risk Results Success': props<{ processId: string; results: ClientRiskMatrixResultsResponseDto }>(),
    'Save Risk Results Failure': props<{ processId: string; error: string; code?: string }>(),

    // The Compliance Officer's observations (US-005, US-009), each saved apart from the form on its own endpoint
    'Load Observation': props<{ processId: string; kind: ProcessObservationKind }>(),
    'Load Observation Success': props<{ processId: string; kind: ProcessObservationKind; text: string | null }>(),
    'Load Observation Failure': props<{ processId: string; kind: ProcessObservationKind; error: string }>(),

    'Save Observation': props<{ processId: string; kind: ProcessObservationKind; text: string | null }>(),
    'Save Observation Success': props<{ processId: string; kind: ProcessObservationKind; text: string | null }>(),
    'Save Observation Failure': props<{ processId: string; kind: ProcessObservationKind; error: string; code?: string }>(),

    // Screenings
    'Load Screenings': props<{ processId: string }>(),
    'Load Screenings Success': props<{ screenings: GroupedScreeningsDto[] }>(),
    'Load Screenings Failure': props<{ error: string }>(),

    'Run Screenings': props<{ processId: string }>(),
    'Run Screenings Success': props<{ response: ScreeningsRunResponseDto }>(),
    'Run Screenings Failure': props<{ error: string }>(),

    // World Check One cases (each with one or more PDFs)
    'Load Worldcheck Cases': props<{ processId: string }>(),
    // finalStatus: the process-level World-Check status (COMPLETED, FAILED or null while still running);
    // missingTargets: the people/companies whose World-Check screening failed.
    'Load Worldcheck Cases Success': props<{
      cases: WorldCheckOneCaseDto[]; finalStatus: string | null; missingTargets: MissingTargetDto[];
    }>(),
    'Load Worldcheck Cases Empty': props<{ finalStatus: string | null; missingTargets: MissingTargetDto[] }>(),
    'Load Worldcheck Cases Failure': props<{ error: string }>(),

    // Statistics
    'Load Process Statistics': props<{ managementCompanyId: string; filters?: ProcessStatisticsFilters }>(),
    'Load Process Statistics Success': props<{ statistics: KycProcessStatisticsDto }>(),
    'Load Process Statistics Failure': props<{ error: string }>(),

    // KYC Final Report PDF (generated on COMPLETED)
    'Load Kyc Report': props<{ processId: string }>(),
    'Load Kyc Report Success': props<{ report: KycReportDto }>(),
    'Load Kyc Report Empty': emptyProps(),
    'Load Kyc Report Failure': props<{ error: string }>(),

    // Long-polling for the final KYC report while waiting on backend generation
    'Start Kyc Report Polling': props<{ processId: string }>(),
    'Stop Kyc Report Polling': emptyProps(),

    // Full compliance KYC report (US-003 on US-002): the Compliance Officer's own, generated on COMPLETED and again
    // after every change to their analysis. `poll`: a re-check while one is awaited (it doesn't start the re-checks
    // again); `silent`: a background load (the link refresh, the re-checks) whose failure leaves the section as it is.
    'Load Compliance Report': props<{ processId: string; poll?: boolean; silent?: boolean }>(),
    'Load Compliance Report Success': props<{ processId: string; report: KycComplianceReportDto }>(),
    'Load Compliance Report Empty': props<{ processId: string; poll?: boolean }>(),
    'Load Compliance Report Failure': props<{ processId: string; error: string; silent?: boolean }>(),
    // A save of the analysis on a COMPLETED process: a newer report is on its way.
    'Compliance Report Outdated': props<{ processId: string }>(),
    // The re-checks ran out with the report still awaited (the job is late, or the save changed nothing).
    'Compliance Report Wait Ended': props<{ processId: string }>(),

    // Re-checking the External Screenings while they are still running, so
    // Accept enables without a page reload. Each tick reloads screenings and
    // World-Check cases together, silently (no spinner, no error toast).
    'Start Screenings Polling': props<{ processId: string; intervalMs: number }>(),
    'Stop Screenings Polling': emptyProps(),

    // Entrust signature (client, or client + board member). The client
    // completes the signing ceremony (including a liveness re-check) via a
    // link Entrust emails them - nothing to embed in this app.
    'Load Signature Status': props<{ processId: string }>(),
    'Load Signature Status Success': props<{ status: EntrustSignatureStatusResponseDto }>(),
    'Load Signature Status Empty': emptyProps(),
    'Load Signature Status Failure': props<{ error: string }>(),

    // silent: the caller (the signature dialog) shows the failure itself, so no error toast.
    'Request Signature': props<{ processId: string; body: EntrustSignatureRequestDto; silent?: boolean }>(),
    // mode: the started run is the board member's in CLIENT_AND_BOARD_MEMBER mode (they sign first, US-042).
    'Request Signature Success': props<{
      processId: string; response: EntrustSignatureStartResponseDto; mode?: EntrustSignatureRequestDto['mode'];
    }>(),
    'Request Signature Failure': props<{ error: string; processId?: string; silent?: boolean }>(),

    // Complete the process without collecting signatures. Final on the
    // backend - no signature can be requested afterwards.
    'Skip Signature': props<{ processId: string }>(),
    'Skip Signature Success': props<{ processId: string; status: EntrustSignatureStatusResponseDto }>(),
    'Skip Signature Failure': props<{ error: string }>(),

    // Long-polling for the signature status while it's non-terminal (webhook +
    // polling backstop on the backend - this just keeps the UI tag current).
    'Start Signature Polling': props<{ processId: string }>(),
    'Stop Signature Polling': emptyProps(),

    // Entrust signature evidence folders (Report Evidence section) - restricted
    // to ADMINISTRATION/COMPLIANCE_OFFICER on the backend, loaded on demand by
    // the report-evidence component rather than eagerly for every viewer.
    'Load Evidence Folders': props<{ processId: string }>(),
    'Load Evidence Folders Success': props<{ evidenceFolders: EntrustSignatureEvidenceFolderDto[] }>(),
    'Load Evidence Folders Failure': props<{ error: string }>(),

    // Client consent evidence (US-015): what completing without signatures needs on a process created since US-014.
    // Its failures are shown where it is uploaded or consulted (no toast). A 404 is no evidence, not a failure.
    'Load Consent Evidence': props<{ processId: string }>(),
    'Load Consent Evidence Success': props<{ processId: string; evidence: KycConsentEvidenceDto | null }>(),
    // Not shown to this user (403, by role): unknown, so nothing is claimed or blocked on it
    'Load Consent Evidence Hidden': props<{ processId: string }>(),
    'Load Consent Evidence Failure': props<{ processId: string; error: string }>(),
    'Upload Consent Evidence': props<{ processId: string; file: File }>(),
    'Upload Consent Evidence Success': props<{ processId: string; evidence: KycConsentEvidenceDto }>(),
    'Upload Consent Evidence Failure': props<{ processId: string; error: string; code?: string }>(),

    // Clear
    'Clear Processes': emptyProps(),
  },
});
